import React, { useState, useRef, useEffect, useCallback, useLayoutEffect, forwardRef, useImperativeHandle } from 'react';
import Dropzone from 'react-dropzone';
import type { DropzoneRef } from 'react-dropzone';
// FileAPI is a CommonJS module - must be loaded via script tag in index.html
// or configured in vite.config.ts optimizeDeps
// @ts-ignore - fileapi doesn't have proper ESM exports
const FileAPI = (window as any).FileAPI || {
  upload: (opts: any) =>
  {
    console.warn('FileAPI not loaded. Please add fileapi to index.html or configure Vite.');
    if (opts.complete) opts.complete(new Error('FileAPI not available'), {} as XMLHttpRequest);
  },
};
import CorgeeStorageHelper from './CorgeeStorageHelper';
import { needsVideoConversion, prepareVideoForUpload } from './videoUploadPrep';
import type { TranscodeProgress } from './videoTranscode';
import './FileUpload.css';

// Interfaces
interface FileUploadItem {
  FileName: string;
  TempFile: string;
  Description: string;
  FileSize: number;
  FileContentType: string;
  FileExtension: string;
  Progress: number;
  AllowedSize: boolean;
  UploadSuccess?: boolean;
  // Set while a video is re-encoded to H.264 before upload (videoUploadPrep)
  Converting?: boolean;
  ConvertProgress?: number;
  ConvertStage?: TranscodeProgress['stage'];
  // Identifies a row added in this session while its async work runs
  ClientId?: string;
}

interface Config {
  url: string;
  token: string;
}

interface WindowSettings {
  js?: {
    settings?: {
      maxAllowedUpload?: number;
    };
  };
}

interface FileUploadProps {
  initialFiles?: FileUploadItem[];
  onChangeFileUpload?: (files: FileUploadItem[] | boolean) => void;
  onRemoveAttachment?: (index: number) => void;
  config: Config;
  isNewRepost?: boolean;
  attachments?: FileUploadItem[];
}

export interface FileUploadRef {
  onDrop: (files: File[] | FileList | DragEvent | ClipboardEvent) => void;
  reset: () => void;
}

interface UploadResponse {
  FileName: string;
  TempFilePath: string;
}

declare global {
  interface Window extends WindowSettings {}
}

// Callers reach onDrop from Dropzone (File[]), Summernote (FileList) and drop/paste events
const toFileArray = (source: unknown): File[] =>
{
  if (!source) return [];
  if (Array.isArray(source)) return source.filter((f): f is File => f instanceof File);

  const transfer =
    (source as DragEvent).dataTransfer ?? (source as ClipboardEvent).clipboardData ?? null;
  if (transfer) return Array.from(transfer.files ?? []);

  if (source instanceof File) return [source];
  if (typeof (source as FileList).length === 'number') return Array.from(source as FileList);

  return [];
};

let nextClientId = 0;

// Status shown next to a video while it is re-encoded before upload
const convertingLabel = (file: FileUploadItem): string =>
{
  switch (file.ConvertStage)
  {
    case 'queued':
      return 'Waiting to optimize video…';
    case 'transcoding':
      return `Optimizing video… ${file.ConvertProgress ?? 0}%`;
    default:
      return 'Preparing video…';
  }
};

const FileUpload = forwardRef<FileUploadRef, FileUploadProps>(({
  initialFiles = [],
  onChangeFileUpload,
  onRemoveAttachment,
  config,
  isNewRepost = false,
  attachments = [],
}, ref) =>
{
  const dropzoneRef = useRef<DropzoneRef>(null);

  const getMaxAllowedUpload = (): number =>
  {
    const maxAttachmentSize =
      window?.js?.settings?.maxAllowedUpload ?? CorgeeStorageHelper.conv_maxAllowedUpload();
    return parseInt(String(maxAttachmentSize)) / 1024;
  };

  const [files, setFiles] = useState<FileUploadItem[]>(initialFiles);
  const [maxAllowedUpload] = useState<number>(getMaxAllowedUpload());
  const [hasError, setHasError] = useState<boolean>(false);
  const isFirstRender = useRef(true);

  useLayoutEffect(() =>
  {
    if (isFirstRender.current)
    {
      isFirstRender.current = false;
      return;
    }
    // A video still converting has not even started uploading: keep the
    // parent in its "uploading" state so the card cannot be saved without it
    onChangeFileUpload?.(files.some(f => f.Converting) ? true : files);
    // onChangeFileUpload intentionally omitted to avoid loops
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files]);

  const validateAttachments = useCallback(
    (attachmentsToValidate: FileUploadItem[]) =>
    {
      const copyAttachments = [...attachmentsToValidate].map(attachment => ({
        ...attachment,
        // Compare numbers: comparing the formatted strings flagged valid files as
        // oversized (e.g. "7263.00" > "51200" lexicographically), which blocked Re-Post.
        AllowedSize: attachment.FileSize / 1024 <= maxAllowedUpload,
      }));

      setFiles(copyAttachments);
      setHasError(copyAttachments.some(x => x.AllowedSize === false));
    },
    [maxAllowedUpload],
  );

  const repostHydratedRef = useRef(false);

  // Hydrate repost attachments once per activation, not on every attachments change
  useEffect(() =>
  {
    if (!isNewRepost)
    {
      repostHydratedRef.current = false;
      return;
    }

    if (repostHydratedRef.current) return;
    repostHydratedRef.current = true;

    if (attachments.length > 0)
    {
      validateAttachments(attachments);
    }
  }, [isNewRepost, attachments, validateAttachments]);

  // Rows are found by ClientId, not position: a row removed while another
  // is still converting or uploading must not shift that one's updates
  const updateItem = useCallback(
    (clientId: string | undefined, changes: Partial<FileUploadItem>) =>
    {
      // Hydrated repost rows have no ClientId and never receive updates
      if (!clientId) return;

      setFiles(prevFiles =>
        prevFiles.map(f => (f.ClientId === clientId ? { ...f, ...changes } : f)),
      );
    },
    [],
  );

  const uploadFileByAPI = useCallback(
    (dataFileUpload: File, itemFile: FileUploadItem) =>
    {
      if (!itemFile.AllowedSize)
      {
        setHasError(true);
        return;
      }

      // Use relative path so Vite dev proxy intercepts the request and avoids CORS.
      // config.url is absolute (e.g. "http://localhost/Corgee.Web"), extracting the
      // pathname keeps the IIS virtual-directory prefix intact.
      const uploadUrl = (() =>
      {
        const absolute = `${config.url.replace(/\/$/, '')}/api/conversation/uploadfile`;
        try
        {
          return new URL(absolute).pathname;
        }
        catch
        {
          return absolute;
        }
      })();

      FileAPI.upload({
        url: uploadUrl,
        headers: { Authorization: `Bearer ${config.token}` },
        files: { file: dataFileUpload },
        progress: (evt: ProgressEvent) =>
        {
          const progress = Math.min(100, parseInt(String((100.0 * evt.loaded) / evt.total)));
          updateItem(itemFile.ClientId, { Progress: progress > 99 ? 99 : progress });
        },
        complete: (err: Error | null, xhr: XMLHttpRequest) =>
        {
          if (!err)
          {
            const apiResponse: UploadResponse = JSON.parse(xhr.response);
            updateItem(itemFile.ClientId, {
              FileName: apiResponse.FileName,
              TempFile: apiResponse.TempFilePath,
              UploadSuccess: true,
              Progress: 100,
            });
          }
          else
          {
            updateItem(itemFile.ClientId, { UploadSuccess: false });
          }

          setFiles(prevFiles =>
          {
            setHasError(prevFiles.some(x => x.AllowedSize === false));
            return prevFiles;
          });
        },
      });
    },
    [config, updateItem],
  );

  // Videos Edge cannot play (HEVC/AV1 from phones) are re-encoded to H.264
  // first, so every viewer gets a file that plays right away
  const prepareAndUpload = useCallback(
    async (file: File, itemFile: FileUploadItem) =>
    {
      if (!itemFile.AllowedSize || !(await needsVideoConversion(file)))
      {
        uploadFileByAPI(file, itemFile);
        return;
      }

      const id = itemFile.ClientId;
      updateItem(id, { Converting: true, ConvertProgress: 0, ConvertStage: 'loading-core' });

      const ready = await prepareVideoForUpload(file, p =>
      {
        updateItem(id, { ConvertProgress: Math.round((p.ratio ?? 0) * 100), ConvertStage: p.stage });
      });

      // The converted copy is a different file: .mp4, new size and type
      const converted: FileUploadItem = {
        ...itemFile,
        FileSize: ready.size,
        FileContentType: ready.type,
        FileExtension: ready.name.substr(ready.name.lastIndexOf('.')).toLowerCase(),
        AllowedSize: ready.size / 1024 <= maxAllowedUpload,
      };

      updateItem(id, {
        Converting: false,
        ConvertProgress: undefined,
        ConvertStage: undefined,
        FileSize: converted.FileSize,
        FileContentType: converted.FileContentType,
        FileExtension: converted.FileExtension,
        AllowedSize: converted.AllowedSize,
      });

      uploadFileByAPI(ready, converted);
    },
    [maxAllowedUpload, updateItem, uploadFileByAPI],
  );

  const onDrop = useCallback(
    (filesUpload: File[] | FileList | DragEvent | ClipboardEvent) =>
    {
      const droppedFiles = toFileArray(filesUpload);
      if (droppedFiles.length === 0) return;

      onChangeFileUpload?.(true);

      const newItems = droppedFiles.map(file =>
      {
        const name = file.name.substr(0, file.name.lastIndexOf('.')) || file.name;
        const ext = file.name.substr(file.name.lastIndexOf('.'));

        const itemFile: FileUploadItem = {
          FileName: '',
          TempFile: '',
          Description: name,
          FileSize: file.size,
          FileContentType: file.type,
          FileExtension: ext.toLowerCase(),
          Progress: 0,
          AllowedSize: file.size / 1024 <= maxAllowedUpload,
          ClientId: `upload-${++nextClientId}`,
        };

        return { file, itemFile };
      });

      // The updater stays pure: StrictMode runs it twice in development, and
      // starting uploads from inside it sent every file twice
      setFiles(prevFiles => [...prevFiles, ...newItems.map(n => n.itemFile)]);

      newItems.forEach(({ file, itemFile }) =>
      {
        prepareAndUpload(file, itemFile);
      });
    },
    [maxAllowedUpload, onChangeFileUpload, prepareAndUpload],
  );

  // Parents that keep the uploader mounted (e.g. "Save and New") must clear it explicitly
  const reset = useCallback(() =>
  {
    setFiles([]);
    setHasError(false);
  }, []);

  useImperativeHandle(ref, () => ({ onDrop, reset }), [onDrop, reset]);

  const onOpenClick = () =>
  {
    dropzoneRef.current?.open();
  };

  const onRemoveFile = (index: number) =>
  {
    setFiles(prevFiles =>
    {
      const updatedFiles = prevFiles.filter((_, i) => i !== index);
      const hasErrorInFiles = updatedFiles.some(x => x.AllowedSize === false);

      setHasError(hasErrorInFiles);
      onRemoveAttachment?.(index);

      return updatedFiles;
    });
  };

  const styleRed: React.CSSProperties = {
    color: 'red',
    fontSize: '12px',
    marginLeft: '5px',
    marginBottom: '5px',
  };

  return (
    <div>
      <Dropzone ref={dropzoneRef} onDrop={onDrop} noClick noKeyboard>
        {({ getRootProps, getInputProps, isDragActive }) => (
          <div
            {...getRootProps()}
            className={`corgee_com_file-uploader-dropzone-area ${isDragActive ? 'active-dropzone' : ''}`}
            onClick={onOpenClick}
          >
            <input {...getInputProps()} />
            <div className="corgee_com_file-uploader-dropzone-area-text">
              <span>Drop files here to upload.</span>
            </div>
          </div>
        )}
      </Dropzone>

      <div className="corgee_com_file-uploader-btn-container">
        <button
          type="button"
          onClick={onOpenClick}
          className="corgee_com_button corgee_com_button-default"
        >
          Add files
        </button>
      </div>

      <div className="corgee_com_file-uploader-uploaded-files-container">
        {files.length > 0 && (
          <ul className="corgee_com_file-uploader-uploaded-files">
            {files.map((file, index) => (
              <li
                key={`file-upload-${index}`}
                className={`corgee_com_file-uploader-uploaded-file${file.AllowedSize === false ? ' file-size-error' : ''}`}
              >
                <div className="corgee_com_file-uploader-file-wrapper">
                  <span
                    className="corgee_com_file-uploader-uploaded-file-name"
                    title={file.Description}
                  >
                    {file.Description}
                  </span>
                  <span className="corgee_com_file-uploader-uploaded-file-size">
                    ({Math.floor(file.FileSize / 1024).toLocaleString('en')}KB)
                  </span>
                  {file.Converting && (
                    <span className="corgee_com_file-uploader-converting">
                      {convertingLabel(file)}
                    </span>
                  )}
                  {file.Progress < 100 && !file.Converting && (
                    <div className="corgee_com_file-uploader-progress-container">
                      <span
                        className="corgee_com_file-uploader-progress-bar"
                        style={{
                          width: `${file.Progress}%`,
                          backgroundColor: '#fff',
                        }}
                      />
                    </div>
                  )}
                  <span
                    className="corgee_com_file-uploader-remove-file-btn"
                    onClick={() => onRemoveFile(index)}
                  >
                    <i className="icon-incorrect-icon" />
                  </span>
                </div>
              </li>
            ))}
            {hasError && (
              <div
                className="corgee_com_file-uploader-file-wrapper"
                style={{ width: '100%', height: '16px' }}
              >
                <span style={styleRed}>
                  Some files exceeded allowed size (Max: {maxAllowedUpload.toLocaleString('en')} KB)
                </span>
              </div>
            )}
          </ul>
        )}
      </div>
    </div>
  );
});

FileUpload.displayName = 'FileUpload';

export default FileUpload;
